import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { getDownloadURL, ref, Storage, uploadBytesResumable } from '@angular/fire/storage';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { NGXLogger } from 'ngx-logger';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { AccountingService } from '../../../../services/accounting.service';
import { AuthService } from '../../../../services/auth.service';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as groupActions from '../../../../state/actions/group.actions';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { ChipComponent, ChipColor } from '../../../components/inputs/chip/chip.component';
import { DialogService } from '../../../components/dialogs/dialog.service';
import { AlertType } from '../../../components/dialogs/confirm.dialog.model';
import { UtilsService } from '../../../../services/utils.service';
import { AppExceptionModel } from '../../../../models/app-exception.model';

export interface ReceiptExpense {
  id: string;
  file: File;
  fileName: string;
  isScanning: boolean;
  isSaving?: boolean;
  isSaved: boolean;
  error?: string;
  form: FormGroup;
  receiptUrl?: string;
}

@Component({
  selector: 'app-upload-receipts',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BtnBackComponent,
    ChipComponent,
    RouterLink,
  ],
  templateUrl: './upload-receipts.component.html',
})
export class UploadReceiptsComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private store: Store<AppState> = inject(Store<AppState>);
  private auth: AuthService = inject(AuthService);
  private accountingService: AccountingService = inject(AccountingService);
  private toastr: ToastrService = inject(ToastrService);
  private dialogService: DialogService = inject(DialogService);
  private utilsService: UtilsService = inject(UtilsService);
  private storage: Storage = inject(Storage);
  private logger = inject(NGXLogger);

  groupId: string | null = null;
  userId: string | null = null;
  groupName = 'Group';
  group: any = null;
  isAiEnabled = true;

  receipts: ReceiptExpense[] = [];
  chipColor = ChipColor;

  private destroy$ = new Subject<void>();

  constructor() {
    this.groupId = this.route.snapshot.paramMap.get('id');
    this.userId = this.auth.getUid() || null;
  }

  ngOnInit(): void {
    if (!this.groupId) {
      this.toastr.error('Group ID not found');
      this.router.navigate(['..'], { relativeTo: this.route });
      return;
    }

    this.store.select(groupSelector.getExtractedReceipt)
      .pipe(takeUntil(this.destroy$))
      .subscribe((extraction) => {
        if (extraction && extraction.receiptId) {
          const item = this.receipts.find((r) => r.id === extraction.receiptId);
          if (item) {
            item.isScanning = false;
            item.form.enable();
            if (extraction.error) {
              this.logger.error('Receipt scan failure:', extraction.errorMessage);
              item.error = 'AI failed to read PDF receipt. Please fill details manually.';
              this.toastr.error(`AI extraction failed for ${item.fileName}`);
              let errMsg = 'AI was unable to extract details from this receipt. Please verify the file format and size, and try again, or enter details manually.';
              if (extraction.errorMessage) {
                errMsg = extraction.errorMessage;
              }
              this.utilsService.onShowAlert({
                title: 'Receipt Scan Failed',
                message: `${item.fileName}: ${errMsg}`,
              } as AppExceptionModel, false);
            } else {
              item.form.patchValue({
                amount: extraction.amount || '',
                currency: extraction.currency?.toLowerCase() || this.group?.currency?.toLowerCase() || 'usd',
                conversionRate: 1,
                date: (extraction.date && typeof extraction.date === 'string')
                  ? extraction.date.substring(0, 10)
                  : new Date().toISOString().substring(0, 10),
                description: extraction.description || '',
                from: extraction.from || '',
                to: extraction.to || '',
                tags: (extraction.tags && Array.isArray(extraction.tags))
                  ? extraction.tags.join(', ')
                  : (typeof extraction.tags === 'string' ? extraction.tags : ''),
              });
              this.toastr.success(`AI scanned details for ${item.fileName}`);
            }
            this.store.dispatch(groupActions.clearExtractedReceipt());
          }
        }
      });

    this.store
      .select(groupSelector.getGroupById(this.groupId))
      .pipe(takeUntil(this.destroy$))
      .subscribe((group) => {
        if (group) {
          this.group = group;
          this.groupName = group.name;
          this.isAiEnabled = group.isAiEnabled !== false; // defaults to true
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = 10 - this.receipts.length;
    if (remainingSlots <= 0) {
      this.toastr.warning('Maximum upload limit of 10 receipts reached.');
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    filesToUpload.forEach((file) => {
      if (file.type !== 'application/pdf') {
        this.toastr.error(`${file.name} is not a PDF file.`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.toastr.error(`${file.name} exceeds the 5MB size limit.`);
        return;
      }

      const id = Math.random().toString(36).substring(2);
      const expenseItem: ReceiptExpense = {
        id,
        file,
        fileName: file.name,
        isScanning: false,
        isSaved: false,
        form: this.createExpenseForm(),
      };

      this.receipts.push(expenseItem);

      // Auto-scan if AI is enabled
      if (this.isAiEnabled) {
        this.scanReceipt(expenseItem);
      }
    });

    // Clear input
    event.target.value = '';
  }

  createExpenseForm(data?: any): FormGroup {
    return new FormGroup({
      amount: new FormControl(data?.amount || '', [
        Validators.required,
        Validators.min(0.01),
      ]),
      currency: new FormControl(data?.currency || this.group?.currency?.toLowerCase() || 'usd', [Validators.required]),
      conversionRate: new FormControl(data?.conversionRate || 1, [
        Validators.required,
        Validators.min(0.000001),
      ]),
      date: new FormControl(
        data?.date || new Date().toISOString().substring(0, 10),
        [Validators.required]
      ),
      description: new FormControl(data?.description || '', [
        Validators.required,
        Validators.minLength(3),
      ]),
      from: new FormControl(data?.from || ''),
      to: new FormControl(data?.to || '', [Validators.required]),
      tags: new FormControl(data?.tags || ''),
    });
  }

  async scanReceipt(item: ReceiptExpense) {
    if (item.isScanning || item.isSaved) return;

    const isAvailable = await firstValueFrom(this.store.select(groupSelector.isAiCreditsAvailable));
    if (!isAvailable) {
      const buyNow = await this.dialogService.confirm({
        title: 'Out of AI Credits',
        description: 'Your group has run out of AI credits. Please subscribe or buy more credits to continue using the AI receipt scanner.',
        action: 'Buy Now',
        cancelText: 'Buy Later',
      });
      if (buyNow && this.groupId) {
        this.router.navigate(['/school', this.groupId, 'billing', 'addonsubscribe']);
      }
      return;
    }

    item.isScanning = true;
    item.isSaving = false;
    item.error = undefined;
    item.form.disable();

    try {
      if (!item.receiptUrl) {
        item.receiptUrl = await this.uploadFile(item);
      }

      const extension = item.file.name.split('.').pop() || 'pdf';
      const fileName = `${item.id}.${extension}`;
      const storageRef = ref(this.storage, `groups/${this.groupId}/receipts/batch_${this.userId}/${fileName}`);
      const gcsPath = `gs://${storageRef.bucket}/${storageRef.fullPath}`;

      if (this.groupId) {
        this.store.dispatch(groupActions.extractReceipt({
          groupId: this.groupId,
          gcsPath: gcsPath,
          mimeType: item.file.type,
          receiptId: item.id,
        }));
      } else {
        this.toastr.error('Group ID is missing. Cannot scan.');
        item.isScanning = false;
        item.form.enable();
      }
    } catch (err: any) {
      console.error(err);
      item.error = 'AI failed to read PDF receipt. Please fill details manually.';
      this.toastr.error(`AI extraction failed for ${item.fileName}`);
      item.isScanning = false;
      item.form.enable();
    }
  }

  removeFile(item: ReceiptExpense) {
    this.receipts = this.receipts.filter((r) => r.id !== item.id);
  }

  async saveExpense(item: ReceiptExpense) {
    if (item.form.invalid || item.isSaved || !this.groupId) return;

    item.isSaving = true;
    item.form.disable();

    try {
      if (!item.receiptUrl) {
        item.receiptUrl = await this.uploadFile(item);
      }

      const tagsArray = item.form.value.tags
        ? item.form.value.tags
            .split(',')
            .map((t: string) => t.trim().toLowerCase())
            .filter(Boolean)
        : [];

      const entry = {
        amount: parseFloat(item.form.value.amount),
        currency: item.form.value.currency,
        conversionRate: item.form.value.currency !== (this.group?.currency || 'USD').toLowerCase()
          ? parseFloat(item.form.value.conversionRate || 1)
          : 1,
        date: item.form.value.date,
        description: item.form.value.description,
        type: 'expense',
        payerName: item.form.value.from || '',
        paidTo: item.form.value.to,
        recordedBy: this.userId,
        tags: tagsArray,
        receiptUrl: item.receiptUrl,
      };

      await this.accountingService.addLedgerEntry(this.groupId, entry);
      item.isSaved = true;
      this.toastr.success(`Expense saved for ${item.fileName}`);
    } catch (err: any) {
      this.toastr.error(err.message || 'Failed to save expense');
      item.form.enable();
    } finally {
      item.isSaving = false;
    }
  }

  private uploadFile(item: ReceiptExpense): Promise<string> {
    return new Promise((resolve, reject) => {
      const extension = item.file.name.split('.').pop() || 'pdf';
      const fileName = `${item.id}.${extension}`;
      const storageRef = ref(this.storage, `groups/${this.groupId}/receipts/batch_${this.userId}/${fileName}`);
      const metadata = { contentType: item.file.type };
      const uploadTask = uploadBytesResumable(storageRef, item.file, metadata);

      uploadTask.on(
        'state_changed',
        null,
        (error) => reject(error),
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then(
            (downloadURL) => resolve(downloadURL),
            (err) => reject(err)
          );
        }
      );
    });
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }
}
